import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  Lock as LockIcon,
  People as PeopleIcon,
  CheckBox as SubtasksIcon,
  ChatBubbleOutline as CommentIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useDraggable } from '@dnd-kit/core';
import UserAvatar from '../common/UserAvatar';
import { UnassignedCircle } from './AssigneePicker';
import { PRIORITY_COLORS, TASK_VISIBILITY } from '../../utils/taskConstants';
import { getDueChipProps } from '../../utils/dueDate';

const MetaItem = ({ icon, children }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375 }}>
    {icon}
    <Typography component="span" sx={{ fontSize: '0.6875rem' }}>
      {children}
    </Typography>
  </Box>
);

/**
 * Kanban card (Tasks PRD §4). Draggable between columns unless completed.
 * Clicking anywhere opens the task dialog (the DnD pointer sensor uses an
 * activation distance so plain clicks don't start a drag).
 */
const TaskCard = ({ task, assignee, clientName, onClick, overlay = false }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    disabled: task.completed || overlay
  });

  const due = getDueChipProps(task.dueDate);
  const subtasks = task.subtasks || [];
  const subtasksDone = subtasks.filter((s) => s.done).length;
  const commentCount = Math.max(0, task.commentCount || 0);
  const urlCount = (task.urls || []).length;

  return (
    <Paper
      ref={overlay ? undefined : setNodeRef}
      {...(overlay ? {} : listeners)}
      {...(overlay ? {} : attributes)}
      onClick={overlay ? undefined : onClick}
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        cursor: overlay ? 'grabbing' : 'pointer',
        boxShadow: overlay
          ? '0 8px 20px rgba(9,30,66,0.35), 0 0 1px rgba(9,30,66,0.31)'
          : '0 1px 1px rgba(9,30,66,0.25), 0 0 1px rgba(9,30,66,0.31)',
        transition: 'box-shadow 150ms',
        userSelect: 'none',
        touchAction: 'none',
        opacity: isDragging ? 0 : task.completed ? 0.55 : 1,
        bgcolor: task.completed ? '#fafafa' : '#fff',
        '&:hover': overlay ? {} : {
          boxShadow: '0 4px 8px -2px rgba(9,30,66,0.25), 0 0 1px rgba(9,30,66,0.31)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.Medium,
            flexShrink: 0
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            flex: 1,
            minWidth: 0,
            wordWrap: 'break-word',
            color: task.completed ? 'text.secondary' : 'text.primary'
          }}
        >
          {task.title}
        </Typography>
        {task.visibility === TASK_VISIBILITY.PRIVATE && (
          <LockIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
        )}
      </Box>

      {clientName && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 1,
            color: 'text.secondary'
          }}
        >
          <PeopleIcon sx={{ fontSize: 12, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.75rem' }} noWrap>
            {clientName}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        {assignee ? (
          <UserAvatar name={assignee.name} color={assignee.color} size={24} />
        ) : (
          <UnassignedCircle size={24} />
        )}
        {due && (
          <Box
            sx={{
              fontSize: '0.6875rem',
              px: 0.875,
              py: 0.25,
              borderRadius: 5,
              bgcolor: due.bg,
              color: due.color,
              fontWeight: due.bold ? 500 : 400,
              whiteSpace: 'nowrap'
            }}
          >
            {due.label}
          </Box>
        )}
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'text.secondary'
          }}
        >
          {subtasks.length > 0 && (
            <MetaItem icon={<SubtasksIcon sx={{ fontSize: 12 }} />}>
              {subtasksDone}/{subtasks.length}
            </MetaItem>
          )}
          {commentCount > 0 && (
            <MetaItem icon={<CommentIcon sx={{ fontSize: 12 }} />}>{commentCount}</MetaItem>
          )}
          {urlCount > 0 && (
            <MetaItem icon={<LinkIcon sx={{ fontSize: 12 }} />}>{urlCount}</MetaItem>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default TaskCard;
